
























































































import { forEach, ifElse } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { Component, Vue } from 'vue-property-decorator';
import {
  fetchScheduledEventMetadatas,
  fetchScheduledHolidayMetadatas,
  fetchPublicHolidays,
} from '@/appsync/service/EventCalendarQueryService';
import EventCalendarPanel from '@/components/organisms/EventCalendarPanel.vue';
import WarningOnAccessFromIosDialog from '@/components/organisms/WarningOnAccessFromIosDialog.vue';
import { checkDateFormatLocaleDate } from '@/containers/globals/Util';
import { refreshStores } from '@/containers/globals/globalSharedService';
import PageNotFound from '@/containers/pages/PageNotFound.vue';
import ErrorMessagePageTemplate from '@/containers/templates/ErrorMessagePageTemplate.vue';
import EventViewTabSelector from '@/containers/templates/EventViewTabSelector.vue';
import PortalAppBar from '@/containers/templates/PortalAppBar.vue';
import { i18n } from '@/plugins/i18n/i18n';
import router from '@/router';
import { commonModule } from '@/store/modules/CommonStore';
import { loginUserModule } from '@/store/modules/LoginUserStore';
import {
  UserInfo,
  LoginInfo,
  ScheduledHolidayMetadataForFront,
  PublicHolidayForFront,
  ScheduledEventMetadataWithDetailForFront,
} from '@/types/type';
import {
  getDisabledRoutes,
  checkBottomAndFetchData,
  isAccessedFromIosDeviceOrSafariBrowser,
} from '@/utils/CommonUtils';
import {
  JST_OFFSET_MINS,
  PAGE_PATH_BY_NAME,
  DisplayPeriod,
} from '@/utils/consts';

enum Option {
  Holiday = 'Holiday',
  Event = 'Event',
}

@Component({
  components: {
    PortalAppBar,
    EventViewTabSelector,
    PageNotFound,
    ErrorMessagePageTemplate,
    EventCalendarPanel,
    WarningOnAccessFromIosDialog,
  },
})
export default class EventCalendar extends Vue {
  private loading: boolean = true;
  private isJobSchedulerEnabled: boolean = false;
  private loginInfo!: LoginInfo;
  private hasSchedulerPermission: boolean = false;
  private scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[] = [];
  private scheduledEventMetadatas: ScheduledEventMetadataWithDetailForFront[] = [];
  private publicHolidays!: PublicHolidayForFront[];

  private notEnabledImgAltMessage: String = 'Page Not Enabled';
  private notEnabledImgName: String = 'page-not-enabled';
  private notEnabledImgWidth: String = '250px';
  private notEnabledImgHeight: String = '186px';

  private isAccessingFromJST: boolean = false;
  private showAccessFromIosOrSafariWarningDialog: boolean = false;

  public async created(): Promise<void> {
    this.loginInfo = commonModule.loginInfo;
    await refreshStores(this.loginInfo);
    this.hasSchedulerPermission = (this as any).$hasPermission(
      'menus.scheduler'
    );
    const disabledRoutes = await getDisabledRoutes(
      this.loginInfo.tenant,
      this.loginInfo.landscape,
      this.loginInfo.loginName
    );

    if (!disabledRoutes.includes(PAGE_PATH_BY_NAME.eventCalendar)) {
      this.redirectIfInvalidParams();
      this.isJobSchedulerEnabled = true;

      const isAccessedFromNotRecommendedEnv: boolean = isAccessedFromIosDeviceOrSafariBrowser();
      if (isAccessedFromNotRecommendedEnv) {
        this.showAccessFromIosOrSafariWarningDialog = true;
      }

      this.isAccessingFromJST =
        new Date().getTimezoneOffset() === JST_OFFSET_MINS;

      await this.setInitialValueForJobScheduler();
    }

    this.loading = false;
  }

  private async setInitialValueForJobScheduler(): Promise<void> {
    this.isJobSchedulerEnabled = true;
    this.scheduledHolidayMetadatas = await fetchScheduledHolidayMetadatas(
      this.loginInfo.tenant,
      this.loginInfo.landscape
    );
    this.scheduledEventMetadatas = await fetchScheduledEventMetadatas(
      this.loginInfo.tenant,
      this.loginInfo.landscape
    );
    this.publicHolidays = await fetchPublicHolidays(i18n.locale);
  }

  private redirectIfInvalidParams() {
    const path = this.$route.path;
    const period = this.$route.params.period;
    const year = this.$route.params.year;
    const month = this.$route.params.month;
    const day = this.$route.params.day;

    const redirectPath = PAGE_PATH_BY_NAME.eventCalendarForMonth;
    if (!(period && year && month && day) && path !== redirectPath) {
      router.push(redirectPath);
      return;
    } else if (path === redirectPath) {
      return;
    }

    const paddedDate = `${year}/${month.padStart(2, '0')}/${day.padStart(
      2,
      '0'
    )}`;
    const pathWithDate = `/eventCalendar/${period}/${paddedDate}`;
    if (
      (!Object.values(DisplayPeriod).includes(period as DisplayPeriod) ||
        !checkDateFormatLocaleDate(paddedDate)) &&
      path !== redirectPath
    ) {
      router.push(redirectPath);
    } else if (path !== pathWithDate) {
      router.push(pathWithDate);
    }
  }

  private get userInfo(): UserInfo {
    return loginUserModule.userInfo;
  }

  private get items(): { [key: string]: any } {
    return {
      event: {
        text: this.$t('jobScheduler.events.event'),
        value: Option.Event,
      },
      holiday: {
        text: this.$t('jobScheduler.holidays.holiday'),
        value: Option.Holiday,
      },
    };
  }

  private redirectTo(item: Option) {
    const newId = uuidv4();
    switch (item) {
      case Option.Holiday:
        return router.push({
          name: 'createHoliday',
          params: { id: newId },
        });
      case Option.Event:
        return router.push({
          name: 'createEvent',
          params: { id: newId },
        });
      default: {
        const inspector: never = item;
        return router.push(PAGE_PATH_BY_NAME.eventCalendar);
      }
    }
  }
}
